"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const common_1 = require("../common");
const CreaseEdgeLoop_1 = require("./CreaseEdgeLoop");
/**
 * A connected component in a Crease object.
 */
class CreaseComponent {
    /**
     * Constructs a CreaseComponent object.
     * @param {number} index The index of this component.
     * @param {CreaseFace[]} faces An array of faces.
     */
    constructor(index, faces) {
        this._index = index;
        this._faces = faces;
        this._edgeLoops = this._createEdgeLoops();
    }
    /**
     * Returns the index of this component.
     * @returns {number} The index of this component.
     */
    get index() {
        return this._index;
    }
    /**
     * Returns an array of faces within this component.
     * @returns {CreaseFace[]} An array of faces.
     */
    get faces() {
        return this._faces;
    }
    /**
     * Returns an array of edge loops. The first loop defines the outer boundary of this component,
     * and subsequent loops define holes in this component.
     * @returns {CreaseEdgeLoop[]} An array of edge loops.
     */
    get edgeLoops() {
        return this._edgeLoops;
    }
    /**
     * Returns an object that can be serialized using `JSON.stringify`.
     * @returns {object} The serializable representation of this component.
     */
    toJSON() {
        return this.faces.map(face => face.index);
    }
    _createEdgeLoops() {
        const clockwiseLoops = [];
        const counterclockwiseLoops = [];
        const edgeVisited = [];
        this.faces.forEach((face) => {
            face.edgeLoops.forEach((loop) => {
                loop.edgeRefs.forEach((edgeRef) => {
                    // If we found an unvisited boundary edge, create a loop of boundary edges.
                    if (edgeRef.isBoundary && !edgeVisited[edgeRef.edge.index]) {
                        const edgeLoop = CreaseComponent._createEdgeLoop(edgeRef, edgeVisited);
                        // Use the signed area of the polygon enclosed by the loop to categorize the
                        // edge loop as clockwise or counterclockwise. We include the curve control
                        // points here because they help in cases where a hole is formed from one
                        // straight edge and one curved edge.
                        const points = edgeLoop.edgeRefs.reduce((p, e) => [...p, e.vertex1.position, ...e.controlPoints], []);
                        const signedArea = common_1.geom.getSignedArea(points);
                        const areaEpsilon = 1e-5;
                        if (signedArea > areaEpsilon) {
                            counterclockwiseLoops.push(edgeLoop);
                        }
                        else {
                            clockwiseLoops.push(edgeLoop);
                        }
                    }
                });
            });
        });
        // A connected set of faces should only have one counterclockwise (outer) loop of
        // boundary edges. In the odd case where we detect one clockwise loop and no
        // counterclockwise loops, we can swap.
        if (counterclockwiseLoops.length === 0 && clockwiseLoops.length === 1) {
            counterclockwiseLoops.push(clockwiseLoops[0]);
            clockwiseLoops.length = 0;
        }
        common_1.check.equals('counterclockwiseLoops.length', counterclockwiseLoops.length, 1);
        return [counterclockwiseLoops[0], ...clockwiseLoops];
    }
    static _createEdgeLoop(edgeRef, edgeVisited) {
        const boundary = [edgeRef];
        edgeVisited[edgeRef.edge.index] = true;
        let vertexEdgeRef = edgeRef.vertex2.incidentEdges.find(er => er.edge === edgeRef.edge);
        if (!vertexEdgeRef) {
            throw new Error('Could not find matching edge.');
        }
        let currentEdgeRef = edgeRef.vertex2.getNextEdgeRef(vertexEdgeRef);
        while (currentEdgeRef.edge !== edgeRef.edge) {
            edgeVisited[currentEdgeRef.edge.index] = true;
            boundary.push(currentEdgeRef);
            /* eslint-disable-next-line no-loop-func */
            vertexEdgeRef = currentEdgeRef.vertex2.incidentEdges.find(er => er.edge === currentEdgeRef.edge);
            if (!vertexEdgeRef) {
                throw new Error('Could not find matching edge.');
            }
            const nextEdgeRef = currentEdgeRef.vertex2.getNextEdgeRef(vertexEdgeRef);
            // At a vertex, the next incident edge after an incoming boundary edge should be an
            // outgoing boundary edge.
            common_1.check.equals('nextEdgeRef.isBoundary', nextEdgeRef.isBoundary, true);
            currentEdgeRef = nextEdgeRef;
        }
        return new CreaseEdgeLoop_1.CreaseEdgeLoop(boundary);
    }
}
exports.CreaseComponent = CreaseComponent;
//# sourceMappingURL=CreaseComponent.js.map